import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CardHoverPreview } from "@/components/Card3D";
import { Plus, Trash2, ChevronLeft, ChevronRight, Search, Power, Pencil } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const PAGE_SIZE = 15;
const RARITY_VALUES = { common: 100, uncommon: 250, rare: 600, epic: 1500, legendary: 4000, goddess: 10000 };

function discountedPrice(base, type, value) {
  base = Number(base) || 0;
  if (type === "pct") return Math.max(0, Math.round(base * (1 - Math.max(0, Math.min(95, Number(value) || 0)) / 100)));
  if (type === "flat") return Math.max(0, base - (Number(value) || 0));
  return base;
}

export default function AdminListings() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "template", ref_id: "", price_credits: 100, discount_type: "none", discount_value: 0 });
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  // New-listing item picker (search / rarity filter / pagination inside the dialog)
  const [itemSearch, setItemSearch] = useState("");
  const [itemRarity, setItemRarity] = useState("all");
  const [itemPage, setItemPage] = useState(0);

  const { data: listings } = useQuery({ queryKey: ["admin-listings"], queryFn: async () => (await api.get("/admin/listings")).data });
  const { data: templates } = useQuery({ queryKey: ["admin-templates"], queryFn: async () => (await api.get("/admin/templates?page_size=1000")).data.items });
  const { data: packs } = useQuery({ queryKey: ["admin-packs"], queryFn: async () => (await api.get("/admin/packs")).data });
  const { data: inv } = useQuery({ queryKey: ["inv-types"], queryFn: async () => (await api.get("/inventory")).data });
  const tokenTypes = useMemo(() => Object.entries(inv?.item_types || {}).map(([type, meta]) => ({ type, ...meta })), [inv]);

  // Edit existing listing (price / discount / active)
  const [editing, setEditing] = useState(null); // listing being edited
  const [editForm, setEditForm] = useState({ price_credits: 0, discount_type: "none", discount_value: 0 });
  const openEdit = (l) => {
    setEditForm({
      price_credits: l.base_price ?? l.price_credits ?? 0,
      discount_type: l.discount_type || "none",
      discount_value: l.discount_value || 0,
    });
    setEditing(l);
  };
  const saveEdit = useMutation({
    mutationFn: async () => (await api.patch(`/admin/listings/${editing.id}`, {
      price_credits: Number(editForm.price_credits) || 0,
      discount_type: editForm.discount_type,
      discount_value: Number(editForm.discount_value) || 0,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-listings"] }); setEditing(null); toast.success("Listing updated"); },
    onError: (e) => toast.error(apiError(e, "Update failed")),
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/listings", {
      type: form.type,
      ref_id: form.ref_id,
      price_credits: Number(form.price_credits) || 0,
      discount_type: form.discount_type === "none" ? null : form.discount_type,
      discount_value: Number(form.discount_value) || 0,
    })).data,
    onSuccess: () => { qc.invalidateQueries(); setOpen(false); toast.success("Listing created"); },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/listings/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-listings"] }); toast.success("Deleted"); },
  });
  const toggle = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/listings/${id}/toggle`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["admin-listings"] }); toast.success(d.active ? "Listing activated" : "Listing paused"); },
    onError: (e) => toast.error(apiError(e, "Couldn't update")),
  });

  const options = useMemo(() => form.type === "template" ? (templates || []) : (packs || []), [form.type, templates, packs]);

  const ITEM_PAGE = 6;
  const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
  const filteredOptions = useMemo(() => {
    let rows = options;
    if (itemSearch.trim()) {
      const q = itemSearch.toLowerCase();
      rows = rows.filter((o) => (o.name || o.title || "").toLowerCase().includes(q));
    }
    if (form.type === "template" && itemRarity !== "all") rows = rows.filter((o) => o.rarity === itemRarity);
    return rows;
  }, [options, itemSearch, itemRarity, form.type]);
  const itemPageCount = Math.max(1, Math.ceil(filteredOptions.length / ITEM_PAGE));
  const itemSafePage = Math.min(itemPage, itemPageCount - 1);
  const shownOptions = filteredOptions.slice(itemSafePage * ITEM_PAGE, itemSafePage * ITEM_PAGE + ITEM_PAGE);
  const selectedItem = options.find((o) => o.id === form.ref_id);

  const resetPicker = (patch) => { setForm((f) => ({ ...f, ...patch })); setItemSearch(""); setItemRarity("all"); setItemPage(0); };

  const itemName = (l) => l.template?.title || l.pack?.name || l.card?.title || l.gear_crate?.name || l.token?.name || (l.type === "gear" ? `${l.ref_id} Gear Crate` : "");
  const filtered = useMemo(() => {
    let rows = listings || [];
    if (filterType !== "all") {
      if (filterType === "active") rows = rows.filter((l) => l.active);
      else if (filterType === "paused") rows = rows.filter((l) => !l.active);
      else rows = rows.filter((l) => l.type === filterType);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((l) => itemName(l).toLowerCase().includes(q));
    }
    return rows;
  }, [listings, filterType, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const shown = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">STORE LISTINGS</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="admin-new-listing-button" className="bg-cyan-400 text-black hover:bg-cyan-300"><Plus className="h-4 w-4 mr-1" /> New Listing</Button></DialogTrigger>
          <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-[0.08em]">NEW LISTING</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Type</Label>
                <Select value={form.type} onValueChange={(v) => resetPicker({ type: v, ref_id: "" })}>
                  <SelectTrigger className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                    <SelectItem value="template">Card Template</SelectItem>
                    <SelectItem value="pack">Pack</SelectItem>
                    <SelectItem value="gear">Gear Crate (random gear)</SelectItem>
                    <SelectItem value="token">Inventory Token</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.type === "token" ? (
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Token type</Label>
                  <Select value={form.ref_id || ""} onValueChange={(v) => { const t = tokenTypes.find((x) => x.type === v); setForm({ ...form, ref_id: v, price_credits: t?.value ?? form.price_credits }); }}>
                    <SelectTrigger data-testid="listing-token-type" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue placeholder="Choose a token…" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 text-white max-h-72">
                      {tokenTypes.map((t) => (
                        <SelectItem key={t.type} value={t.type} data-testid={`listing-token-option-${t.type}`}>{t.name} — {(t.value || 0).toLocaleString()}c</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.ref_id && <div className="text-[11px] text-white/40 mt-1.5">{tokenTypes.find((t) => t.type === form.ref_id)?.desc}</div>}
                </div>
              ) : form.type === "gear" ? (
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Gear rarity (auto-prices the crate)</Label>
                  <Select value={form.ref_id || ""} onValueChange={(v) => setForm({ ...form, ref_id: v, price_credits: RARITY_VALUES[v] ?? form.price_credits })}>
                    <SelectTrigger data-testid="listing-gear-rarity" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10 capitalize"><SelectValue placeholder="Choose rarity…" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 text-white">
                      {["common", "uncommon", "rare", "epic", "legendary", "goddess"].map((r) => (
                        <SelectItem key={r} value={r} className="capitalize">{r} — {RARITY_VALUES[r].toLocaleString()}c</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-white/40 mt-1.5">Buying mints a random {form.ref_id || "—"} gear (weapon/armor/trinket/relic) to the player.</div>
                </div>
              ) : (
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Choose {form.type === "template" ? "a template" : "a pack"}</Label>
                  {selectedItem && <span className="font-mono text-[10px] text-emerald-300 truncate max-w-[55%]" data-testid="listing-selected">✓ {selectedItem.name || selectedItem.title}</span>}
                </div>
                <div className="flex gap-2 mt-1.5">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                    <Input data-testid="listing-item-search" value={itemSearch} onChange={(e) => { setItemSearch(e.target.value); setItemPage(0); }} placeholder="Search…" className="h-9 pl-8 bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm" />
                  </div>
                  {form.type === "template" && (
                    <Select value={itemRarity} onValueChange={(v) => { setItemRarity(v); setItemPage(0); }}>
                      <SelectTrigger data-testid="listing-item-rarity" className="h-9 w-32 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs capitalize"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 text-white">{RARITIES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>

                <div className="mt-2 space-y-1.5 min-h-[230px]">
                  {shownOptions.map((o) => {
                    const sel = form.ref_id === o.id;
                    const img = o.front_image || o.image;
                    return (
                      <CardHoverPreview key={o.id} card={o} side="left">
                        <button
                          type="button"
                          data-testid={`listing-item-${o.id}`}
                          onClick={() => setForm({ ...form, ref_id: o.id, price_credits: form.type === "template" ? (RARITY_VALUES[o.rarity] ?? form.price_credits) : form.price_credits })}
                          className={`w-full flex items-center gap-3 rounded-lg border p-2 text-left transition-colors ${sel ? "border-cyan-300 bg-cyan-300/10" : "border-white/10 hover:bg-white/5"}`}
                        >
                          <div className={`h-10 w-8 shrink-0 rounded overflow-hidden bg-black ${o.rarity ? `rarity-${o.rarity}` : ""}`}>
                            {img && <img src={img} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-white text-sm truncate">{o.name || o.title}</div>
                            {o.rarity && <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">{o.rarity}</div>}
                          </div>
                          {sel && <ChevronRight className="h-4 w-4 text-cyan-300 shrink-0" />}
                        </button>
                      </CardHoverPreview>
                    );
                  })}
                  {filteredOptions.length === 0 && <div className="text-center text-white/40 text-sm py-10">No {form.type === "template" ? "templates" : "packs"} match.</div>}
                </div>

                {filteredOptions.length > ITEM_PAGE && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono text-[10px] text-white/40">{filteredOptions.length} items · page {itemSafePage + 1}/{itemPageCount}</span>
                    <div className="flex gap-2">
                      <Button data-testid="listing-item-prev" type="button" size="sm" variant="outline" disabled={itemSafePage === 0} onClick={() => setItemPage(itemSafePage - 1)} className="border-white/15 text-white/70 h-7"><ChevronLeft className="h-4 w-4" /></Button>
                      <Button data-testid="listing-item-next" type="button" size="sm" variant="outline" disabled={itemSafePage >= itemPageCount - 1} onClick={() => setItemPage(itemSafePage + 1)} className="border-white/15 text-white/70 h-7"><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                  </div>
                )}
              </div>
              )}

              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Price (credits){(form.type === "template" || form.type === "gear") ? " · auto-set by rarity" : form.type === "token" ? " · auto-set by token value" : ""}</Label>
                <Input data-testid="listing-price-input" type="number" min={0} value={form.price_credits} onChange={(e) => setForm({ ...form, price_credits: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Discount</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger data-testid="listing-discount-type" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 text-white">
                      <SelectItem value="none">No discount</SelectItem>
                      <SelectItem value="pct">% off</SelectItem>
                      <SelectItem value="flat">Flat credits off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">{form.discount_type === "pct" ? "Percent (0-95)" : "Amount"}</Label>
                  <Input data-testid="listing-discount-value" type="number" min={0} disabled={form.discount_type === "none"}
                    value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })}
                    className="mt-1 bg-[hsl(220_18%_12%)] border-white/10 disabled:opacity-40" />
                </div>
              </div>
              {form.discount_type !== "none" && (
                <div data-testid="listing-price-preview" className="font-mono text-xs text-white/60 flex items-center gap-2">
                  Buyers pay <span className="line-through text-white/30">{Number(form.price_credits).toLocaleString()}c</span>
                  <span className="text-emerald-300">{discountedPrice(form.price_credits, form.discount_type, form.discount_value).toLocaleString()}c</span>
                </div>
              )}
              <Button data-testid="listing-create-button" onClick={() => create.mutate()} disabled={!form.ref_id || create.isPending} className="w-full bg-cyan-400 text-black hover:bg-cyan-300">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(0); }}>
          <SelectTrigger data-testid="listing-filter-type" className="h-9 w-44 bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 text-white">
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="template">Card Templates</SelectItem>
            <SelectItem value="pack">Packs</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="paused">Paused only</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <Input data-testid="listing-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search item…" className="h-9 w-56 pl-8 bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm" />
        </div>
        <span className="ml-auto font-mono text-[11px] text-white/40" data-testid="listing-count">{filtered.length} listing{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="hud-pill rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
            <tr><th className="px-4 py-3">Type</th><th className="px-4 py-3">Item</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {shown.map((l) => (
              <tr key={l.id} className={`border-t border-white/5 ${l.active ? "" : "opacity-60"}`} data-testid={`listing-row-${l.id}`}>
                <td className="px-4 py-3 uppercase font-mono text-xs text-white/70">{l.type}</td>
                <td className="px-4 py-3 text-white">{l.template?.title || l.pack?.name || l.card?.title || "—"}</td>
                <td className="px-4 py-3 font-mono">
                  {l.discount_type && l.base_price && l.base_price !== l.price_credits ? (
                    <span className="flex items-center gap-1.5">
                      <span className="line-through text-white/30">{l.base_price.toLocaleString()}</span>
                      <span className="text-emerald-300">{l.price_credits.toLocaleString()}</span>
                      <span className="text-[9px] uppercase rounded bg-emerald-400/15 text-emerald-300 px-1 py-0.5">{l.discount_type === "pct" ? `-${l.discount_value}%` : `-${l.discount_value}c`}</span>
                    </span>
                  ) : (
                    <span className="text-amber-300">{l.price_credits.toLocaleString()}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid={`listing-toggle-${l.id}`}
                      checked={!!l.active}
                      disabled={toggle.isPending}
                      onCheckedChange={() => toggle.mutate(l.id)}
                      className="data-[state=checked]:bg-emerald-500"
                    />
                    <span className={`text-xs uppercase tracking-[0.2em] ${l.active ? "text-emerald-300" : "text-white/40"}`} data-testid={`listing-status-${l.id}`}>
                      {l.active ? "Active" : "Paused"}
                    </span>
                    {l.type === "card" && l.claimed && <span className="text-[10px] font-mono text-rose-300/80">claimed</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button data-testid={`listing-edit-${l.id}`} variant="ghost" size="sm" onClick={() => openEdit(l)} className="text-cyan-300"><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`listing-delete-${l.id}`} variant="ghost" size="sm" onClick={() => { if (window.confirm("Delete listing?")) del.mutate(l.id); }} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">No listings.</td></tr>}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <span className="font-mono text-[11px] text-white/40">Page {safePage + 1} / {pageCount}</span>
          <div className="flex gap-2">
            <Button data-testid="listing-prev-page" size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="border-white/15 text-white/70 h-8"><ChevronLeft className="h-4 w-4" /></Button>
            <Button data-testid="listing-next-page" size="sm" variant="outline" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} className="border-white/15 text-white/70 h-8"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      {/* EDIT LISTING dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-[0.08em]">EDIT LISTING</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3" data-testid="listing-edit-dialog">
              <div className="font-mono text-[11px] text-white/50 uppercase tracking-[0.2em]">{editing.type} · {itemName(editing)}</div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Base price (credits)</Label>
                <Input data-testid="listing-edit-price" type="number" min={0} value={editForm.price_credits} onChange={(e) => setEditForm({ ...editForm, price_credits: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Discount</Label>
                  <Select value={editForm.discount_type} onValueChange={(v) => setEditForm({ ...editForm, discount_type: v })}>
                    <SelectTrigger data-testid="listing-edit-discount-type" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 text-white">
                      <SelectItem value="none">No discount</SelectItem>
                      <SelectItem value="pct">% off</SelectItem>
                      <SelectItem value="flat">Flat credits off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">{editForm.discount_type === "pct" ? "Percent (0-95)" : "Amount"}</Label>
                  <Input data-testid="listing-edit-discount-value" type="number" min={0} disabled={editForm.discount_type === "none"} value={editForm.discount_value} onChange={(e) => setEditForm({ ...editForm, discount_value: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10 disabled:opacity-40" />
                </div>
              </div>
              <div data-testid="listing-edit-preview" className="font-mono text-xs text-white/60 flex items-center gap-2">
                Buyers pay
                {editForm.discount_type !== "none" && <span className="line-through text-white/30">{Number(editForm.price_credits).toLocaleString()}c</span>}
                <span className="text-emerald-300">{discountedPrice(editForm.price_credits, editForm.discount_type, editForm.discount_value).toLocaleString()}c</span>
              </div>
              <Button data-testid="listing-edit-save" onClick={() => saveEdit.mutate()} disabled={saveEdit.isPending} className="w-full bg-cyan-400 text-black hover:bg-cyan-300">Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
